// src/pages/AgendaUsersPage.js
// Admin page to manage agenda users and invites
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Alert, Table, Badge, Modal, Form, Spinner, Tabs, Tab } from 'react-bootstrap';
import { callXeroFunction } from '../firebase.js';

export default function AgendaUsersPage() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [newInviteLink, setNewInviteLink] = useState('');

  // Load users and invites
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [usersRes, invitesRes] = await Promise.all([
        callXeroFunction('getAgendaUsers', {}, { method: 'GET' }),
        callXeroFunction('getAgendaInvites', {}, { method: 'GET' })
      ]);

      setUsers(usersRes.users || []);
      setInvites(invitesRes.invites || []);
    } catch (err) {
      setError('Falha ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create invite
  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    setError('');
    setNewInviteLink('');

    try {
      const result = await callXeroFunction('createAgendaInvite', {
        email: inviteEmail || null
      });

      setNewInviteLink(result.invite.link);
      setSuccess('Convite criado com sucesso!');
      loadData();
    } catch (err) {
      setError('Falha ao criar convite: ' + err.message);
    } finally {
      setCreatingInvite(false);
    }
  };

  // Cancel invite
  const handleCancelInvite = async (id) => {
    if (!window.confirm('Cancelar este convite?')) return;

    try {
      await callXeroFunction('cancelAgendaInvite', {}, { method: 'DELETE', id });
      setSuccess('Convite cancelado');
      loadData();
    } catch (err) {
      setError('Falha ao cancelar convite: ' + err.message);
    }
  };

  // Deactivate user
  const handleDeactivateUser = async (id) => {
    if (!window.confirm('Desativar este usuario? Ele sera deslogado e nao podera mais acessar.')) return;

    try {
      await callXeroFunction('deactivateAgendaUser', {}, { method: 'PUT', id, action: 'deactivate' });
      setSuccess('Usuario desativado');
      loadData();
    } catch (err) {
      setError('Falha ao desativar usuario: ' + err.message);
    }
  };

  // Reactivate user
  const handleReactivateUser = async (id) => {
    try {
      await callXeroFunction('reactivateAgendaUser', {}, { method: 'PUT', id, action: 'reactivate' });
      setSuccess('Usuario reativado');
      loadData();
    } catch (err) {
      setError('Falha ao reativar usuario: ' + err.message);
    }
  };

  // Copy invite link
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Link copiado!');
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge bg="warning">Pendente</Badge>;
      case 'used': return <Badge bg="success">Usado</Badge>;
      case 'cancelled': return <Badge bg="secondary">Cancelado</Badge>;
      default: return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gerenciar Agenda</h2>
          <Button variant="primary" onClick={() => {
            setShowInviteModal(true);
            setInviteEmail('');
            setNewInviteLink('');
          }}>
            + Criar Convite
          </Button>
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Tabs defaultActiveKey="users" className="mb-3">
            <Tab eventKey="users" title={`Usuarios (${users.length})`}>
              <Card>
                <Card.Body>
                  {users.length === 0 ? (
                    <p className="text-muted text-center py-3">Nenhum usuario cadastrado</p>
                  ) : (
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Email</th>
                          <th>WhatsApp</th>
                          <th>Status</th>
                          <th>Criado em</th>
                          <th>Acoes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => (
                          <tr key={user.id}>
                            <td>{user.name || '-'}</td>
                            <td>{user.email}</td>
                            <td>{user.whatsappNumber || '-'}</td>
                            <td>
                              {user.isActive ? (
                                <Badge bg="success">Ativo</Badge>
                              ) : (
                                <Badge bg="danger">Desativado</Badge>
                              )}
                            </td>
                            <td>{formatDate(user.createdAt)}</td>
                            <td>
                              {user.isActive ? (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeactivateUser(user.id)}
                                >
                                  Desativar
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => handleReactivateUser(user.id)}
                                >
                                  Reativar
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            <Tab eventKey="invites" title={`Convites (${invites.length})`}>
              <Card>
                <Card.Body>
                  {invites.length === 0 ? (
                    <p className="text-muted text-center py-3">Nenhum convite criado</p>
                  ) : (
                    <Table responsive striped hover>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Usado por</th>
                          <th>Criado em</th>
                          <th>Expira em</th>
                          <th>Acoes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invites.map(invite => (
                          <tr key={invite.id}>
                            <td>{invite.email || <span className="text-muted">Qualquer email</span>}</td>
                            <td>{getStatusBadge(invite.status)}</td>
                            <td>{invite.usedByName || '-'}</td>
                            <td>{formatDate(invite.createdAt)}</td>
                            <td>{formatDate(invite.expiresAt)}</td>
                            <td>
                              {invite.status === 'pending' && (
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => copyToClipboard(invite.link)}
                                  >
                                    Copiar Link
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleCancelInvite(invite.id)}
                                  >
                                    Cancelar
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        )}
      </Container>

      {/* Create Invite Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Criar Convite</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newInviteLink ? (
            <div>
              <Alert variant="success">Convite criado com sucesso!</Alert>
              <Form.Group className="mb-3">
                <Form.Label>Link do Convite</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={newInviteLink}
                    readOnly
                  />
                  <Button variant="primary" onClick={() => copyToClipboard(newInviteLink)}>
                    Copiar
                  </Button>
                </div>
              </Form.Group>
              <p className="text-muted small">
                Envie este link para o usuario. O convite expira em 7 dias.
              </p>
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email (opcional)</Form.Label>
                <Form.Control
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Se informado, apenas este email podera usar o convite.
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {newInviteLink ? (
            <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleCreateInvite} disabled={creatingInvite}>
                {creatingInvite ? 'Criando...' : 'Criar Convite'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
